import React, { useState } from "react";
import AsyncSelect from "react-select/async";
import toast from "react-hot-toast";
import { Link as LinkIcon, X } from "react-feather";
import {
  useGetLinkedProductsQuery,
  useLazySearchProductsAllBranchesQuery,
  useLinkProductMutation,
  useUnlinkProductMutation,
} from "../productsApi";

// "Linked in other branches" panel (handoff §9.8/§13.2) - identical pattern
// to LinkedCustomersPanel.jsx: each branch keeps its own fully independent
// product record (own stock/pricing/everything), linking just adds a
// purely identity-level cross-reference on top.
const LinkedProductsPanel = ({ branchId, productCode, readOnly }) => {
  const [showSearch, setShowSearch] = useState(false);
  const { data: linked = [], isLoading } = useGetLinkedProductsQuery({ branchId, productCode });
  const [triggerSearch] = useLazySearchProductsAllBranchesQuery();
  const [linkProduct, { isLoading: isLinking }] = useLinkProductMutation();
  const [unlinkProduct] = useUnlinkProductMutation();

  const loadOptions = async (inputValue) => {
    if (!inputValue || inputValue.trim().length < 2) return [];
    const { data = [] } = await triggerSearch({ query: inputValue.trim(), crossBranch: true });
    return data
      .filter((p) => !(p.branchId === Number(branchId) && p.productCode === Number(productCode)))
      .filter((p) => !linked.some((l) => l.branchId === p.branchId && l.productCode === p.productCode))
      .map((p) => ({
        value: `${p.branchId}-${p.productCode}`,
        label: `${p.productName} · ${p.branchCode} · ${p.productCodeNew}`,
        product: p,
      }));
  };

  const handleLink = async (option) => {
    if (!option) return;
    try {
      await linkProduct({
        branchId,
        productCode,
        targetBranchId: option.product.branchId,
        targetProductCode: option.product.productCode,
      }).unwrap();
      toast.success(`Linked to ${option.product.productName} (${option.product.branchCode})`);
      setShowSearch(false);
    } catch (error) {
      toast.error(error?.message || "Could not link product");
    }
  };

  const handleUnlink = async (sibling) => {
    if (!window.confirm(`Unlink from ${sibling.productName} (${sibling.branchCode})?`)) return;
    try {
      await unlinkProduct({ branchId, productCode }).unwrap();
      toast.success("Unlinked");
    } catch (error) {
      toast.error(error?.message || "Could not unlink");
    }
  };

  return (
    <div className="card mb-4">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Linked in Other Branches</h5>
          {!readOnly && (
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => setShowSearch((v) => !v)}
            >
              <LinkIcon size={14} className="me-1" />
              {showSearch ? "Cancel" : "Link to another branch"}
            </button>
          )}
        </div>

        {showSearch && (
          <div className="mb-3">
            <AsyncSelect
              classNamePrefix="react-select"
              cacheOptions
              loadOptions={loadOptions}
              onChange={handleLink}
              isLoading={isLinking}
              placeholder="Search by name or code (all branches)..."
              noOptionsMessage={({ inputValue }) =>
                inputValue.length < 2 ? "Type at least 2 characters" : "No matching products"
              }
            />
          </div>
        )}

        {isLoading ? (
          <p className="text-muted mb-0">Loading...</p>
        ) : linked.length === 0 ? (
          <p className="text-muted mb-0">
            Not linked to any other branch's product record.
          </p>
        ) : (
          <div className="row">
            {linked.map((sibling) => (
              <div className="col-md-6 mb-2" key={`${sibling.branchId}-${sibling.productCode}`}>
                <div className="border rounded p-2 d-flex justify-content-between align-items-start">
                  {/* getLinked returns ProductLinkSummaryRecord, no RecordGuid -
                      can't deep-link into the recordGuid-keyed edit route, so
                      this is identity display only. */}
                  <div>
                    <strong>{sibling.productName}</strong>
                    <div className="text-muted small">{sibling.branchCode}</div>
                    <div className="text-muted small">{sibling.productCodeNew}</div>
                  </div>
                  {!readOnly && (
                    <button
                      type="button"
                      className="btn btn-link text-danger p-1"
                      title="Unlink"
                      onClick={() => handleUnlink(sibling)}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LinkedProductsPanel;
